import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import { isEmpty } from 'underscore';
import { useSelector } from 'react-redux';
import { AngularContext, AngularServicesContext } from 'react-app';

import { getCollectionLecturePage } from 'redux/actions/collections';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse } from 'redux/selectors/course';
import { getLecturePage, setDestinationLecturePage, setLecturePageAsCached } from 'redux/actions/lecture-pages';
import { getCurrentUser } from 'redux/selectors/users';
import { translateLecturePage } from 'redux/actions/users';
import { getCurrentInstitution } from 'redux/selectors/institutions';

import { gray6 } from 'styles/global_defaults/colors';
import { standardModalWidth } from 'styles/global_defaults/scaffolding';
import { notDesktop } from 'styles/global_defaults/media-queries';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import NvIcon from 'shared/components/nv-icon';
import { LecturePageMode } from '.';
import { LecturePagePreviewContent } from './lecture-page-preview-content';
import { LecturePageUrlParams } from './lecture-page-routes';
import { config } from '../../../config/config.json';

export enum PreviewSource {
  AI_ADD = 'ai_add',
  COPY = 'copy',
  MOVE = 'move',
  NORMAL = 'normal',
}

type PreviewContextValue = {
  lecturePageId: number,
  source: PreviewSource,
  onClosePreviewModal: () => void,
  onAction?: (index: number) => void,
};

export const LecturePagePreviewContext = React.createContext<PreviewContextValue>(null);

const Content = () => {
  const { $state } = useContext(AngularServicesContext);
  const { lecturePageId } = useContext(LecturePagePreviewContext);

  const dispatch = useAppDispatch();

  const lecturePage = useSelector(state => state.models.lecturePages[lecturePageId]);
  const currentUser = useSelector(getCurrentUser);
  const { automatedTranslationEnabled: courseTranslationEnabled, isContentManagementCollection, id: courseId } = useSelector(getCurrentCourse);
  const { cachedLecturePageLookup } = useSelector(state => state.app.lecturePage);
  const { translationPreferenceLanguage } = currentUser;
  const {
    automatedTranslationEnabled: institutionTranslationEnabled,
  } = useSelector(getCurrentInstitution);
  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);
  const {
    id: currentPreviewId,
    gallaryLessonIds,
    catalogId: previewCatalogId,
  } = useSelector(state => state.app.destinationLecturePage);

  const catalogId = !isEmpty(previewCatalogId) ? previewCatalogId : currentCatalogId;
  // Display lessons in gallery mode if the array contains values.
  const hasGallery = !isEmpty(gallaryLessonIds);

  const params: LecturePageUrlParams = useMemo(() => ({
    catalogId,
    lecturePageId,
    mode: LecturePageMode.VIEW,
  }), [catalogId, lecturePageId]);

  const { injectServices } = useContext(AngularContext);

  const [
    CurrentUserManager,
  ] = injectServices([
    'CurrentUserManager',
  ]);

  const styles = css`
    ${hasGallery && css`
      .gallary-arrows {
        position: fixed;
        top: 50%;

        width: 1080px;
        ${notDesktop(css`
          width: 100%;
        `)}
      }
    `}
  `;

  const fetchLecturePage = useCallback(() => dispatch(isContentManagementCollection
    ? getCollectionLecturePage({ lecturePageId })
    : getLecturePage(params)),
  [dispatch, isContentManagementCollection, lecturePageId, params]);

  useEffect(() => {
    // Avoid re-fetching if cached
    const pageCached = cachedLecturePageLookup[lecturePageId];
    if (!pageCached) {
      let beforePromise = null;

      if (
        translationPreferenceLanguage
        && institutionTranslationEnabled
        && courseTranslationEnabled
      ) {
        beforePromise = dispatch(translateLecturePage({
          lecturePageId,
          catalogId,
          language: translationPreferenceLanguage,
        })).then(() => {
          CurrentUserManager.user.updateFromReact({
            translationPreferenceLanguage,
            lastTranslationPreferenceLanguage: translationPreferenceLanguage,
          });
        });
      }

      const requestLecturePage = () => fetchLecturePage().then(resultAction => {
        // Navigate to the home page if the page can't be loaded.
        if (getLecturePage.rejected.match(resultAction)) {
          $state.go('course-home', { catalogId });
          return;
        }
        dispatch(setLecturePageAsCached(lecturePageId));
      });

      if (beforePromise) {
        beforePromise.then(requestLecturePage);
      } else {
        requestLecturePage();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    catalogId,
    lecturePageId,
    translationPreferenceLanguage,
    institutionTranslationEnabled,
    courseTranslationEnabled,
  ]);

  const getNextLessonId = (isNext: boolean) => {
    const currentIndex = gallaryLessonIds.indexOf(currentPreviewId);
    const nextIndex = isNext ? currentIndex + 1 : currentIndex - 1;
    return gallaryLessonIds[nextIndex];
  };

  const handleArrowClick = (isNext: boolean) => {
    const nextId = getNextLessonId(isNext);
    dispatch(setDestinationLecturePage({
      lecturePageId: nextId,
      mode: LecturePageMode.VIEW,
      catalogId: previewCatalogId,
      gallaryIds: gallaryLessonIds,
    }));
  };

  const hasNext = hasGallery && getNextLessonId(true);
  const hasPrevious = hasGallery && getNextLessonId(false);

  return (
    <div css={styles}>
      <LecturePagePreviewContent />
      {hasGallery && (
        <div className='gallary-arrows d-flex justify-content-between px-6'>
          <NvIcon
            icon='arrow-left'
            className={`${!hasPrevious && 'text-gray-5'}`}
            size='small'
            onClick={() => hasPrevious && handleArrowClick(false)}
            aria-disabled={!hasPrevious}
            data-qa={config.pendo.novoAiCourseCreation.icons.arrowLeftIcon}
          />
          <NvIcon
            icon='arrow-right'
            className={`${!hasNext && 'text-gray-5'}`}
            size='small'
            onClick={() => hasNext && handleArrowClick(true)}
            aria-disabled={!hasNext}
            data-qa={config.pendo.novoAiCourseCreation.icons.arrowRightIcon}
          />
        </div>
      )}
    </div>
  );
};

const LecturePagePreviewModal = (props: {
  show: boolean,
  onClose: () => void,
  header?: string,
}) => {
  const [showModal, setShowModal] = useState(props.show);

  const { gallaryLessonIds } = useSelector(state => state.app.destinationLecturePage);
  const hasGallery = !isEmpty(gallaryLessonIds);
  const action = useSelector(state => state.app.destinationLecturePage?.action);

  const modalStyles = css`
  .bs4-modal-body {
    padding: 0;
    .main-panel-scrollable {
      overflow-y: auto;
      overflow-x: hidden;

      ${hasGallery && css`
        width: 800px;
        margin-right: auto;
        margin-left: auto;
      `}
    }
  }
`;

  useEffect(() => {
    setShowModal(props.show);
  }, [props.show]);

  const modalWidth = hasGallery ? 1080 : standardModalWidth;

  const getHeader = (): string => {
    if (props.header) {
      return props.header;
    }
    return action && t.LECTURE_PAGES.COMPONENTS.PICK_LOCATION_TITLE[action.toUpperCase()]();
  };

  return (
    <NvModal
      type={ModalType.DYNAMIC}
      width={modalWidth}
      header={getHeader()}
      headerBgColor={gray6}
      body={<Content />}
      show={showModal}
      onClose={props.onClose}
      modalStyles={modalStyles}
      closeDataQa={config.pendo.novoAiCourseCreation.icons.closeIcon}
    />
  );
};

export default LecturePagePreviewModal;
